import React, { useState } from 'react';
import { Box, Paper, Typography, Stack, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText } from '@mui/material';
import { Edit, Add } from '@mui/icons-material';
import HelpTooltip from '../../../../components/HelpTooltip';

const PresetList = ({ filteredPresets, openPresetModal, onAddPreset, sectionOrder = [] }) => {
  const [selectedPresetIds, setSelectedPresetIds] = useState([]);
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [targetSectionId, setTargetSectionId] = useState('');

  const handleTogglePreset = (presetId) => {
    setSelectedPresetIds(prev =>
      prev.includes(presetId)
        ? prev.filter(id => id !== presetId)
        : [...prev, presetId]
    );
  };

  const handleBulkAdd = () => {
    if (selectedPresetIds.length === 0) return;
    setBulkAddOpen(true);
  };

  const handleConfirmBulkAdd = () => {
    if (!targetSectionId) return;
    selectedPresetIds.forEach(presetId => {
      const numericPresetId = Number(presetId);
      if (isNaN(numericPresetId)) {
        return;
      }
      const newItem = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        presetId: numericPresetId,
        propertyId: null,
      };
      onAddPreset(targetSectionId, newItem);
    });
    setSelectedPresetIds([]);
    setTargetSectionId('');
    setBulkAddOpen(false);
  };

  return (
    <>
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Select Presets</Typography>
          <Stack direction="row" alignItems="center">
            <Button
              size="small"
              variant="contained"
              startIcon={<Add />}
              onClick={handleBulkAdd}
              disabled={selectedPresetIds.length === 0}
            >
              Bulk Add to Section
            </Button>
            <HelpTooltip title="Add selected presets to a section." />
          </Stack>
        </Stack>
        <Box sx={{ maxHeight: '600px', overflowY: 'auto', mb: 2 }}>
          {filteredPresets.length === 0 ? (
            <Paper sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">No presets found. Try adjusting your search or filters.</Typography>
            </Paper>
          ) : (
            filteredPresets.map(preset => (
              <Paper
                key={preset.id}
                draggable
                onDragStart={(e) => e.dataTransfer.setData('presetId', preset.id)}
                sx={{
                  p: 1.5,
                  mb: 1,
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' },
                  '&:hover': { bgcolor: '#f0f8ff' },
                  transition: '0.2s',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  userSelect: 'none',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    size="small"
                    checked={selectedPresetIds.includes(preset.id)}
                    onChange={() => handleTogglePreset(preset.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {preset.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {preset._normalizedCategory} • {preset.description || 'No description'}
                    </Typography>
                  </Box>
                </Box>
                <Stack direction="row" alignItems="center">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPresetModal(preset);
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <HelpTooltip title="Edit this preset's name, pricing, category, and more." />
                </Stack>
              </Paper>
            ))
          )}
        </Box>
      </Box>
      <Dialog open={bulkAddOpen} onClose={() => setBulkAddOpen(false)}>
        <DialogTitle>Add Presets to Section</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Target Section</InputLabel>
            <Select
              value={targetSectionId}
              onChange={(e) => setTargetSectionId(e.target.value)}
              disabled={sectionOrder.length === 0}
            >
              {sectionOrder.length === 0 ? (
                <MenuItem disabled value="">
                  No sections available
                </MenuItem>
              ) : (
                sectionOrder.map(section => (
                  <MenuItem key={section.id} value={section.id}>
                    {section.label || section.id}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkAddOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmBulkAdd}
            variant="contained"
            disabled={!targetSectionId || sectionOrder.length === 0}
          >
            Add Presets
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PresetList;